import type { MouseEvent } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { ActionText } from '@components/action-text';
import { Badge } from '@components/badge';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { useDeleteModal } from '@components/generic-modals/delete-modal';
import { Header } from '@components/header';
import { IconEdit } from '@components/icons/edit';
import { IconLock } from '@components/icons/lock';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconPortal } from '@components/icons/portal';
import { IconReadMore } from '@components/icons/read-more';
import { IconReplace } from '@components/icons/replace';
import { IconSecurity } from '@components/icons/security';
import { IconTrash } from '@components/icons/trash';
import { ListCard } from '@components/list-card';
import { RelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { AnimatedSwitch } from '@components/switch/animated-switch';
import { Text } from '@components/text';
import { ValueList } from '@components/value-list';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { pxToRem } from '@utils/converters';

import { useDeletePortal, usePortals, useUpdatePortal } from 'api/portals/hooks';
import { Portal } from 'api/portals/requests';
import { routePaths } from 'ui/authenticated/navigation';

import { useCreatePortalModal } from './create-portal-modal';
import { usePortalFilters } from './hooks/use-portal-filters';

export const Portals = () => {
  const { t } = useScopedTranslate(locale);
  const portalsQuery = usePortals();
  const isLoading = portalsQuery.isLoading;
  const data = portalsQuery.data;

  return (
    <>
      <Header
        actions={<HeaderActions />}
        breadcrumbs={<Breadcrumb items={[{ label: t('portals') }]} />}
      />
      <PageContent>
        {isLoading && <RelativeSpinner />}
        {data && <Details data={data} />}
      </PageContent>
    </>
  );
};

const HeaderActions = () => {
  const { t } = useScopedTranslate(locale);
  const showCreatePortalModal = useCreatePortalModal();
  return (
    <Stack orientation="horizontal" space={2} align="center">
      <Button
        variant="secondary"
        text={t('learn')}
        iconLeft={<IconReadMore />}
        iconRight={<IconOpenNewTab color="buttonIconPositive" size={16} />}
        href="https://help.journeys.50skills.app/en/articles/217612-journeys-portal"
        target="_blank"
      />
      <Button
        variant="primary"
        text={t('createPortal')}
        iconLeft={<IconPortal />}
        onClick={() => showCreatePortalModal()}
      />
    </Stack>
  );
};

const Details = ({ data }: { data: Portal[] }) => {
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();

  const handlePortalClick = (portal: Portal) => {
    navigate(
      generatePath(routePaths.portalDetail, {
        portalId: portal.shortId,
        '*': 'layout',
      })
    );
  };

  return (
    <Stack space={2}>
      {data.map((portal) => (
        <ListCard
          data-testid="portals-list-card"
          key={portal.shortId}
          onClick={() => handlePortalClick(portal)}
          spaceX={6}
          spaceY={4}
          title={portal.name}
          description={portal?.description}
          icon={<IconPortal />}
          actions={
            <Stack orientation="horizontal" space={4} align="center">
              <Stack orientation="horizontal" space={4} align="center" style={{ minWidth: 0 }}>
                <ActionText.ExternalLink
                  href={portal?.url ?? `${process.env.VITE_PORTAL_URL}/${portal.slugPath}`}
                  color="textHelper"
                  hoverColor="elementBlue100"
                  onClick={(e: MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
                  style={{
                    minWidth: 0,
                    maxWidth: pxToRem(400),
                  }}
                >
                  <Stack orientation="horizontal" space={1} align="center">
                    <Text
                      font="regular12"
                      text={portal?.url ?? `${process.env.VITE_PORTAL_URL}/${portal.slugPath}`}
                      style={{
                        ...lineClamp('-webkit-inline-box', 1),
                        minWidth: 0,
                        color: 'currentColor',
                      }}
                    />
                    <IconOpenNewTab size={16} />
                  </Stack>
                </ActionText.ExternalLink>
                {portal.securityType === 'azure_sso' && (
                  <IconSecurity
                    color="elementIndigo100"
                    size={18}
                    tooltip={t('azureSSOProtected')}
                  />
                )}
                {(portal.securityType === 'password' ||
                  (!portal.securityType && portal.isPasswordProtected)) && (
                  <IconLock color="elementPurple50" size={18} tooltip={t('passwordProtected')} />
                )}
                {portal.isDefault && <Badge variant="indigo" size="md" text={t('default')} />}
                <Badge
                  variant={portal.isActive ? 'green' : 'neutral'}
                  size="md"
                  text={portal.isActive ? t('active') : t('disabled')}
                />
              </Stack>
              <Separator orientation="horizontal" />
              <ValueList
                items={[
                  { value: portal.numLinks, label: t('links') },
                  {
                    value: portal.numForms,
                    label: t('forms'),
                  },
                ]}
              />
              <PortalActions portal={portal} handleEdit={handlePortalClick} />
            </Stack>
          }
        />
      ))}
    </Stack>
  );
};

const PortalActions = ({
  portal,
  handleEdit,
}: {
  portal: Portal;
  handleEdit: (portal: Portal) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const deletePortalMutation = useDeletePortal();
  const updatePortalMutation = useUpdatePortal(portal.shortId);
  const { removeSelectedPortal } = usePortalFilters();
  const showDeleteModal = useDeleteModal();

  const handleSwitchChange = async (value: boolean) => {
    await updatePortalMutation.mutateAsync({ isActive: value });
  };

  const handleMakeDefault = async () => {
    await updatePortalMutation.mutateAsync({ isDefault: true });
  };

  return (
    <Menu buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }} placement="left-start">
      <Menu.Item label={t('editDetails')} icon={<IconEdit />} onSelect={() => handleEdit(portal)} />
      <Menu.ItemLink
        to={portal?.url ?? `${process.env.VITE_PORTAL_URL}/${portal.slugPath}`}
        isExternal
        icon={<IconPortal />}
        label={
          <Stack orientation="horizontal" space={1} align="center">
            <Text font="regular14" text={t('openPortal')} />
            <IconOpenNewTab color="elementPurple50" size={16} />
          </Stack>
        }
      />
      {!portal.isDefault && (
        <Menu.Item
          label={t('makeDefault')}
          onSelect={handleMakeDefault}
          icon={<IconReplace color="elementPurple80" />}
        />
      )}
      <Menu.Separator />
      <Menu.Item
        label={t('active')}
        onSelect={() => handleSwitchChange(!portal.isActive)}
        extraIcon={
          <Stack space={1} align="center">
            <AnimatedSwitch checked={portal.isActive} />
          </Stack>
        }
      />
      <Menu.Separator />
      <Menu.Item
        label={t('deletePortal')}
        isDisabled={portal.isDefault}
        tooltip={portal.isDefault ? t('deletePortalDisabled') : undefined}
        icon={<IconTrash />}
        variant="danger"
        onSelect={() =>
          showDeleteModal({
            title: t('deletePortal'),
            description: styledTranslate({
              key: 'deletePortalDescription',
              variables: {
                portal: {
                  text: portal.name,
                  font: 'bold16',
                },
              },
              translations: locale,
            }),
            buttonText: t('core.delete'),
            hasUserConfirmation: true,
            onConfirm: () => {
              deletePortalMutation.mutateAsync(portal.shortId);
              removeSelectedPortal(portal.shortId);
            },
          })
        }
      />
    </Menu>
  );
};
const locale = {
  en: {
    portals: 'Portals',
    learn: 'Learn',
    createPortal: 'Create portal',
    info: "Set up and manage your company's portals to easily share forms, links, and resources with your team.",
    active: 'Active',
    disabled: 'Disabled',
    links: 'Links',
    forms: 'Forms',
    editDetails: 'Edit details',
    openPortal: 'Open portal',
    deletePortal: 'Delete portal',
    passwordProtected: 'Password protected',
    azureSSOProtected: 'Azure SSO protected',
    deletePortalDescription:
      'Are you sure you want to permanently delete the portal: {portal}?\n\nThe portal and all form links, and settings will be permanently removed and will no longer be accessible.',

    deletePortalDisabled: 'This is the default portal and cannot be deleted',
    default: 'Default',
    makeDefault: 'Make default portal',
  },
};
